import { createContext, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { doc } from "firebase/firestore";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { db } from "../lib/firebase";
import { useFirestoreDocOnce } from "../hooks/useFirestoreDocOnce";
import type { Event } from "@event-planner/shared";

interface EventContextValue {
  event: Event | null;
  loading: boolean;
  /** Re-runs the event fetch. Call after updateEvent to refresh config. */
  refetch: () => void;
}

const EventContext = createContext<EventContextValue>({
  event: null,
  loading: true,
  refetch: () => {},
});

export function useEvent() {
  return useContext(EventContext);
}

export function EventLayout() {
  const { eventId } = useParams<{ eventId: string }>();
  const docRef = useMemo(
    () => (eventId ? doc(db, "events", eventId) : null),
    [eventId],
  );
  const { data: event, loading, refetch } = useFirestoreDocOnce<Event>(docRef);

  const value = useMemo(
    () => ({ event, loading, refetch }),
    [event, loading, refetch],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" component="h1">
          Event not found
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This event may have been deleted or you don&apos;t have access.
        </Typography>
      </Box>
    );
  }

  return (
    <EventContext.Provider value={value}>
      <Outlet />
    </EventContext.Provider>
  );
}
